import { Card, Skeleton, SkeletonAvatar, SkeletonRegion } from '@parsina/framework';
import { RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// One `EncounterCard` in placeholder, for the card grid's first load. The
// PageHeader and toolbar render for real; the grid fills with these at the
// card's real geometry — `p-5`, 48px avatar, name + type lines, the status
// chip, the divider, then the 2×2 meta grid. The list view uses the kit's
// `SkeletonRow` inside the real table instead.
// ─────────────────────────────────────────────────────────────────────────────

const MetaCell = ({ w }) => (
  <div className="bg-background rounded-lg px-2.5 py-1.5 flex flex-col gap-1.5">
    <Skeleton width={44} height={10} />
    <Skeleton width={w} height={12} />
  </div>
);

export const EncounterCardSkeleton = ({ i = 0, label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={label ?? t('common.state.loading')}
      className="bg-white rounded-2xl border-[1.5px] border-border p-5 flex flex-col gap-3.5 shadow-card"
    >
      <div className="flex items-start gap-3">
        <SkeletonAvatar size={48} />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
          <Skeleton width={`${50 + ((i * 17) % 30)}%`} height={14} />
          <Skeleton width={`${30 + ((i * 11) % 24)}%`} height={11} />
        </div>
        <Skeleton width={64} height={22} radius={RADIUS.pill} />
      </div>
      <div className="h-px bg-border" />
      <div className="grid grid-cols-2 gap-2">
        <MetaCell w={72} />
        <MetaCell w={88} />
        <MetaCell w={56} />
        <MetaCell w={64} />
      </div>
    </SkeletonRegion>
  );
};

/**
 * One `MobileEncounterCard`: a Card whose tappable body is `p-3` — 40px
 * avatar, name + type lines, the status chip, the chevron — over the 2-column
 * meta grid. The mobile list's first load draws `count` of these under the
 * real MobileHeader and filter row.
 */
const MobileEncounterCardSkeleton = ({ i }) => (
  <Card aria-hidden style={{ padding: 0, overflow: 'hidden' }}>
    <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <SkeletonAvatar size={40} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <Skeleton width={`${46 + ((i * 17) % 30)}%`} height={14} />
          <Skeleton width={`${28 + ((i * 11) % 22)}%`} height={11} />
        </div>
        <Skeleton width={60} height={22} radius={RADIUS.pill} />
        <Skeleton width={16} height={16} radius={RADIUS.chip} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
        {[72, 56].map((w, j) => (
          <div key={j} style={{ borderRadius: '0.5rem', padding: '0.375rem 0.5rem', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Skeleton width={40} height={10} />
            <Skeleton width={w} height={12} />
          </div>
        ))}
      </div>
    </div>
  </Card>
);

export const EncountersMobileListSkeleton = ({ count = 4, label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="encounters-loading" style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {Array.from({ length: count }, (_, i) => <MobileEncounterCardSkeleton key={i} i={i} />)}
    </SkeletonRegion>
  );
};

export default EncounterCardSkeleton;
